
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormDraft } from "@/hooks/useFormDraft";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useInventoryStore } from "@/store/api/inventoryStore";
import { toast } from "sonner";

interface AddItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Item name must be at least 2 characters.",
  }),
  size: z.string().min(1, {
    message: "Size is required.",
  }),
  stock: z.coerce.number().int().min(0, {
    message: "Stock must be a positive number.",
  }),
  price: z.coerce.number().min(0, {
    message: "Price must be a positive number.",
  }),
  category: z.enum(["suits", "equipment", "accessories"], {
    required_error: "Please select a category.",
  }),
});

type FormValues = z.infer<typeof formSchema>;

export function AddItemDialog({ open, onOpenChange }: AddItemDialogProps) {
  const { t } = useTranslation(["inventory", "common"]);
  const { addItem } = useInventoryStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      size: "",
      stock: 0,
      price: 0,
      category: "suits",
    },
  });

  // Brouillon persisté (Annuler efface, Escape/overlay conserve).
  const { clearDraft } = useFormDraft("add-item", form, {
    isEmpty: (v) => !v.name && !v.size,
  });

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      await addItem({
        name: values.name,
        size: values.size,
        stock: values.stock,
        price: values.price,
        category: values.category,
        available: values.stock,
        reserved: 0,
      });
      toast.success(t('itemAddedSuccess'));
      clearDraft();
      onOpenChange(false);
      form.reset();
    } catch (error) {
      // Le store lève en cas d'échec : pas de toast de succès sans écriture.
      console.error('[AddItemDialog] addItem:', error);
      toast.error(t('itemAddedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('addNewItem')}</DialogTitle>
          <DialogDescription>
            {t('addNewItemDescription')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('itemName')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('itemNamePlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('size')}</FormLabel>
                    <FormControl>
                      <Input placeholder="S, M, L, XL, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('category')}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('selectCategory')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="suits">{t('suits')}</SelectItem>
                        <SelectItem value="equipment">{t('equipment')}</SelectItem>
                        <SelectItem value="accessories">{t('accessories')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="stock"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('stock')}</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('price')}</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <DialogFooter>
              {/* Annulation explicite : le brouillon est jeté. */}
              <Button
                type="button"
                variant="outline"
                onClick={() => { clearDraft(); onOpenChange(false); }}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('saving', { ns: 'common' }) : t('save', { ns: 'common' })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
